import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAppTranslation } from '../../i18n/useAppTranslation';
import { useToast } from '../../contexts/ToastContext';
import { integrationApiService } from '../../services/integrationApi';
import type { LightspeedProvisionResult } from '../../services/integrationApi';
import { ConfirmDialog, EmptyState, InlineMessage, LoadingState, SectionCard, StatusChip } from '../ui';
import { isNoisySpecLabel } from '../mapping/mappingModel';

const T = 'integrations.lightspeedConfig.provision';

export interface LightspeedProductFieldsProps {
  integrationId: string;
  onOpenMapping?: () => void;
}

/**
 * Product Fields tab for a Lightspeed integration (flag `lightspeed_store_config`): pick catalog
 * specs and create a Lightspeed item custom field for each, mapped to it. Nothing is written to
 * Lightspeed until the store confirms.
 */
const LightspeedProductFields: React.FC<LightspeedProductFieldsProps> = ({ integrationId, onOpenMapping }) => {
  const { t } = useAppTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [search, setSearch] = useState('');
  const [confirming, setConfirming] = useState(false);
  const [result, setResult] = useState<LightspeedProvisionResult | null>(null);

  const query = useQuery({
    queryKey: ['lightspeed-custom-fields', integrationId],
    queryFn: () => integrationApiService.getLightspeedCustomFields(integrationId),
  });
  const data = query.data;
  const mappedBySpec = useMemo(() => {
    const names = new Map((data?.fields || []).map((f) => [f.customFieldID, f.name]));
    return new Map((data?.mappings || []).map((m) => [m.specLabel.toLowerCase(), names.get(m.customFieldId) || m.name || m.customFieldId]));
  }, [data]);
  const specs = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.specs || [])
      .filter((s) => !isNoisySpecLabel(s.label, s.productCount, true))
      .filter((s) => !q || s.label.toLowerCase().includes(q))
      .sort((a, b) => b.productCount - a.productCount);
  }, [data, search]);

  const provision = useMutation({
    mutationFn: () => integrationApiService.provisionLightspeedCustomFields(integrationId, Array.from(picked)),
    onSuccess: (res) => {
      setConfirming(false);
      setResult(res);
      setPicked(new Set());
      if (res.failed.length === 0) showToast(t(`${T}.done`, { count: res.created.length + res.reused.length }), 'success');
      queryClient.invalidateQueries({ queryKey: ['lightspeed-custom-fields', integrationId] });
      queryClient.invalidateQueries({ queryKey: ['integration', integrationId] });
    },
    onError: (err: Error) => {
      setConfirming(false);
      showToast(err.message || t(`${T}.failed`), 'error');
    },
  });

  const toggle = (label: string) => {
    const next = new Set(picked);
    if (next.has(label)) next.delete(label);
    else next.add(label);
    setPicked(next);
  };

  return (
    <SectionCard
      title={t(`${T}.title`)}
      subtitle={t(`${T}.subtitle`)}
      actions={
        <Button variant="contained" size="small" disabled={picked.size === 0 || provision.isPending} onClick={() => setConfirming(true)}>
          {t(`${T}.create`, { count: picked.size })}
        </Button>
      }
    >
      {query.isLoading ? (
        <LoadingState message={t(`${T}.loading`)} />
      ) : query.error ? (
        <InlineMessage severity="error">{(query.error as Error).message}</InlineMessage>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {result && (result.created.length > 0 || result.reused.length > 0) && (
            <InlineMessage severity="success">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                <span>{t(`${T}.result`, { created: result.created.length, reused: result.reused.length })}</span>
                {onOpenMapping && (
                  <Button size="small" onClick={onOpenMapping}>
                    {t(`${T}.openMapping`)}
                  </Button>
                )}
              </Box>
            </InlineMessage>
          )}
          {result && result.failed.length > 0 && (
            <InlineMessage severity="error">
              {t(`${T}.someFailed`, { count: result.failed.length })}
              <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                {result.failed.map((f) => (
                  <li key={f.specLabel}>
                    {f.specLabel}: {f.error}
                  </li>
                ))}
              </Box>
            </InlineMessage>
          )}
          <Typography variant="body2" color="text.secondary">
            {t(`${T}.typesNote`)}
          </Typography>
          <TextField
            size="small"
            placeholder={t(`${T}.search`)}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ maxWidth: 360 }}
          />
          {specs.length === 0 ? (
            <EmptyState size="compact" title={t(`${T}.none`)} description={t(`${T}.noneBody`)} />
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox" />
                  <TableCell>{t(`${T}.spec`)}</TableCell>
                  <TableCell>{t(`${T}.sample`)}</TableCell>
                  <TableCell align="right">{t(`${T}.products`)}</TableCell>
                  <TableCell>{t(`${T}.status`)}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {specs.map((spec) => {
                  const mappedTo = mappedBySpec.get(spec.label.toLowerCase());
                  return (
                    <TableRow key={spec.label} hover={!mappedTo}>
                      <TableCell padding="checkbox">
                        <Checkbox
                          size="small"
                          disabled={!!mappedTo}
                          checked={picked.has(spec.label)}
                          onChange={() => toggle(spec.label)}
                          inputProps={{ 'aria-label': spec.label }}
                        />
                      </TableCell>
                      <TableCell>{spec.label}</TableCell>
                      <TableCell sx={{ color: 'text.secondary', maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {spec.sample}
                      </TableCell>
                      <TableCell align="right">{spec.productCount}</TableCell>
                      <TableCell>
                        {mappedTo ? (
                          <StatusChip tone="positive" label={t(`${T}.mappedTo`, { name: mappedTo })} />
                        ) : (
                          <StatusChip tone="neutral" label={t(`${T}.notInLightspeed`)} />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </Box>
      )}
      <ConfirmDialog
        open={confirming}
        title={t(`${T}.confirmTitle`, { count: picked.size })}
        message={t(`${T}.confirmBody`, { names: Array.from(picked).join(', ') })}
        confirmLabel={t(`${T}.confirm`)}
        loading={provision.isPending}
        onConfirm={() => provision.mutate()}
        onClose={() => setConfirming(false)}
      />
    </SectionCard>
  );
};

export default LightspeedProductFields;
